/**
 * SERP — Offline read cache for financial reports.
 *
 * Persists the last 5 successful report responses per (companyId, endpoint,
 * params) in localStorage so a flaky mobile connection or an outright
 * offline moment still shows the last-known TB / P&L / BS / cash-flow.
 *
 * Contract:
 *   - fetchReport(key, fetcher) → { data, source: 'live' | 'offline', stale, ts }
 *     · Tries the network first via `fetcher()`.
 *     · On success stores the response in the ring buffer + returns
 *       `{ source: 'live' }`.
 *     · On failure (network error / 5xx thrown) returns the newest cached
 *       response with `{ source: 'offline', stale: true, ts: <ISO> }`.
 *   - listCached(companyId) → recent [{ endpoint, params, ts, size }] for UX.
 *
 * Storage layout:
 *   localStorage["serp:reports:<companyId>"] = {
 *      "<endpoint>::<paramsKey>": { data, ts, size },
 *      … up to 5 keys (oldest evicted) …
 *   }
 */
const MAX_ENTRIES = 5;
const NS = "serp:reports:";

function _load(companyId) {
  try {
    const raw = localStorage.getItem(NS + companyId);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function _save(companyId, blob) {
  try {
    localStorage.setItem(NS + companyId, JSON.stringify(blob));
  } catch (_e) {
    // Quota exceeded → drop oldest and retry once.
    const entries = Object.entries(blob).sort((a, b) => (a[1].ts || 0).localeCompare(b[1].ts || 0));
    if (entries.length > 1) {
      const trimmed = Object.fromEntries(entries.slice(1));
      try { localStorage.setItem(NS + companyId, JSON.stringify(trimmed)); } catch (_ee) { /* give up */ }
    }
  }
}

function _key(endpoint, params) {
  const parts = Object.keys(params || {})
    .sort()
    .filter((k) => params[k] !== undefined && params[k] !== null && params[k] !== "")
    .map((k) => `${k}=${params[k]}`)
    .join("&");
  return `${endpoint}::${parts}`;
}

function _pack(blob) {
  const entries = Object.entries(blob).sort((a, b) => (b[1].ts || "").localeCompare(a[1].ts || ""));
  return Object.fromEntries(entries.slice(0, MAX_ENTRIES));
}

export async function fetchReport(companyId, endpoint, params, fetcher) {
  const key = _key(endpoint, params);
  try {
    const data = await fetcher();
    const blob = _load(companyId);
    const size = typeof data === "object" ? JSON.stringify(data).length : 0;
    blob[key] = { data, ts: new Date().toISOString(), size, endpoint, params };
    _save(companyId, _pack(blob));
    return { data, source: "live", stale: false, ts: blob[key].ts };
  } catch (err) {
    const blob = _load(companyId);
    const cached = blob[key];
    if (cached) {
      return { data: cached.data, source: "offline", stale: true, ts: cached.ts, error: err };
    }
    throw err;
  }
}

export function listCached(companyId) {
  const blob = _load(companyId);
  return Object.values(blob)
    .sort((a, b) => (b.ts || "").localeCompare(a.ts || ""))
    .map(({ endpoint, params, ts, size }) => ({ endpoint, params, ts, size }));
}

export function clearReports(companyId) {
  try { localStorage.removeItem(NS + companyId); } catch (_e) { /* ignore */ }
}
